import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, switchMap, throwError, timer } from 'rxjs';

import { SKIP_TENANT_AUTH_REFRESH } from './auth-refresh-context';
import { TenantSessionRefreshService } from '../services/session/tenant-session-refresh.service';
import { TenantAuthService } from '../services/tenant-auth.service';

@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  private readonly auth = inject(TenantAuthService);
  private readonly sessionRefresh = inject(TenantSessionRefreshService);
  private readonly cookieSettleDelayMs = 250;
  private redirecting = false;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error) => {
        if (!this.shouldAttemptRefresh(req, error)) {
          return throwError(() => error);
        }

        return this.sessionRefresh.refresh().pipe(
          switchMap(() => timer(this.cookieSettleDelayMs)),
          switchMap(() => next.handle(this.withCredentialedFetch(req))),
          catchError((refreshError) => {
            return this.redirectOnlyWhenSessionIsGone(refreshError);
          }),
        );
      }),
    );
  }

  private shouldAttemptRefresh(req: HttpRequest<unknown>, error: unknown): boolean {
    if (req.headers.has('X-Financial-Confirmation')) return false;
    return typeof window !== 'undefined'
      && !this.redirecting
      && !req.context.get(SKIP_TENANT_AUTH_REFRESH)
      && error instanceof HttpErrorResponse
      && this.isAuthExpired(req, error)
      && !this.isBypassedAuthEndpoint(req.url)
      && !this.isNonCriticalSessionEndpoint(req.url)
      && !this.isLoginPage();
  }

  private withCredentialedFetch(req: HttpRequest<unknown>): HttpRequest<unknown> {
    return req.credentials === 'include'
      ? req
      : req.clone({ withCredentials: false, credentials: 'include' });
  }

  private isAuthExpired(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
    const errorCode = String(error.error?.error ?? error.error?.errorCode ?? '').toUpperCase();
    const message = String(error.error?.message ?? error.message ?? '').toLowerCase();
    const url = String(error.url ?? req.url ?? '').toLowerCase();
    const authEndpoint = url.includes('/api/v1/user/');

    return (
      error.status === 401 ||
      errorCode === 'AUTHORIZATION_FAILED' ||
      (error.status === 403 && authEndpoint && message.includes('authorization failed'))
    );
  }

  private isBypassedAuthEndpoint(url: string): boolean {
    return (
      url.includes('/api/v1/user/login') ||
      url.includes('/api/v1/user/pre-authenticate') ||
      url.includes('/api/v1/user/verify') ||
      url.includes('/api/v1/user/send-token') ||
      url.includes('/api/v1/user/refresh') ||
      url.includes('/api/v1/user/logout') ||
      url.includes('/api/v1/user/mfa/authenticator/')
    );
  }

  private isNonCriticalSessionEndpoint(url: string): boolean {
    return url.includes('/api/v1/sessions/touch');
  }

  private isLoginPage(): boolean {
    const path = window.location.pathname;
    return path === '/login' || path.startsWith('/login/');
  }

  private redirectOnlyWhenSessionIsGone(error: unknown): Observable<never> {
    return this.auth.verifySession().pipe(
      catchError(() => of({ authenticated: false })),
      switchMap((session) => {
        if (!session?.authenticated) {
          this.redirectToLogin();
        }
        return throwError(() => error);
      }),
    );
  }

  private redirectToLogin(): void {
    if (this.redirecting || typeof window === 'undefined') {
      return;
    }

    this.redirecting = true;
    this.auth.redirectToLogin(this.currentReturnPath());
  }

  private currentReturnPath(): string {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return path && path !== '/' ? path : '/dashboard';
  }
}
